import {
  NODE_X, NODE_Y, nodePoint, nodeOf, canStandAtPoint,
} from './pathfind';
import { FOOT_OFFSET_Y, TILE_SIZE } from './events';

/**
 * Which tiles a player can actually get to, starting from the spawn points.
 *
 * WHY THIS IS WORTH HAVING. A world can be perfectly valid and still contain a
 * shop nobody can enter, a courtyard walled off by accident, or a corridor too
 * narrow to walk down. None of those are errors — the map loads, the game runs,
 * and the only symptom is a player standing outside a door that will not let
 * them in. Today that ships silently and reads as a pathfinding bug.
 *
 * IT REUSES THE PATHFINDER RATHER THAN ASKING "IS THIS TILE SOLID". That is the
 * whole reason it is trustworthy: `canStandAtPoint` mirrors the server's box
 * test, so this honours the 18-versus-16 rule from commit 402d6d1 where a
 * one-tile-wide corridor is deliberately impassable even though every tile in
 * it is empty. A naive tile flood fill would call that corridor reachable and
 * quietly disagree with the game.
 *
 * The node grid is asymmetric for the reasons pathfind.js explains at length —
 * half a tile across, a full tile down.
 */

/** Four-way, matching the pathfinder. Diagonals would let the fill slip through
 *  a corner the player cannot actually cut. */
const STEPS = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/**
 * @param {{walls: number[], width: number, height: number}} grid tile units
 * @param {{x: number, y: number}[]} spawns world units, entity space
 * @returns {Set<number>} tile indices (`ty * width + tx`) a player can reach
 */
export function reachableTiles(grid, spawns = []) {
  const reached = new Set();
  if (!grid?.walls || !grid.width || !grid.height) return reached;

  const maxNx = Math.ceil((grid.width * TILE_SIZE) / NODE_X);
  const maxNy = Math.ceil((grid.height * TILE_SIZE) / NODE_Y);

  const seen = new Set();
  const queue = [];
  const key = (nx, ny) => ny * (maxNx + 2) + nx;

  const push = (nx, ny) => {
    if (nx < 0 || ny < 0 || nx > maxNx || ny > maxNy) return;
    const id = key(nx, ny);
    if (seen.has(id)) return;

    const point = nodePoint(nx, ny);
    if (!canStandAtPoint(grid, point.x, point.y)) return;

    seen.add(id);
    queue.push([nx, ny]);
  };

  // Spawns are entity positions; collision happens at the FEET. Getting this
  // wrong shifts every start point 7 units up, which on a tight map is the
  // difference between starting inside a doorway and inside the wall above it.
  spawns.forEach((spawn) => {
    const start = nodeOf(spawn.x, spawn.y + FOOT_OFFSET_Y);
    push(start.nx, start.ny);

    // A spawn pad can sit somewhere the foot box does not fit — the server
    // nudges clear of geometry on arrival. Seed the neighbours too, so a
    // slightly-off pad does not report the whole world unreachable.
    STEPS.forEach(([dx, dy]) => push(start.nx + dx, start.ny + dy));
  });

  while (queue.length > 0) {
    const [nx, ny] = queue.pop();

    const point = nodePoint(nx, ny);
    const tx = Math.floor(point.x / TILE_SIZE);
    const ty = Math.floor(point.y / TILE_SIZE);
    if (tx >= 0 && ty >= 0 && tx < grid.width && ty < grid.height) {
      reached.add(ty * grid.width + tx);
    }

    STEPS.forEach(([dx, dy]) => push(nx + dx, ny + dy));
  }

  return reached;
}

/**
 * Tiles that look walkable but are not actually reachable.
 *
 * What the editor shades. Solid tiles are excluded — a wall being unreachable
 * is not news, and shading every wall would bury the three tiles that matter.
 */
export function unreachableTiles(grid, spawns = []) {
  const reached = reachableTiles(grid, spawns);
  const stranded = [];

  for (let i = 0; i < grid.walls.length; i += 1) {
    if (grid.walls[i] === 0 && !reached.has(i)) stranded.push(i);
  }

  return stranded;
}

export default reachableTiles;
